import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  Image, 
  FileType, 
  MoreVertical, 
  Check, 
  X, 
  Pencil,
  Download,
  Loader2,
  FolderOpen,
  RefreshCw,
  Eye,
  AlertTriangle,
  Search,
  Globe,
  Link,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import IngestionScrutinyPanel, { FLAG_PATTERNS, ExtractedItem, FlagType } from './IngestionScrutinyPanel';

interface TenantAssetsTabProps {
  tenantId: string;
}

interface Asset {
  id: string;
  tenant_id: string;
  asset_type: string;
  file_url: string;
  extracted_json: any;
  ingestion_status: string;
  flagged_items: any;
  created_at: string;
}

const assetTypeIcons: Record<string, React.ReactNode> = {
  logo: <Image className="w-4 h-4" />,
  brand_guideline: <FileText className="w-4 h-4" />,
  font: <FileType className="w-4 h-4" />,
  faq: <FileText className="w-4 h-4" />,
  document: <FileText className="w-4 h-4" />,
  website_profile: <Globe className="w-4 h-4" />,
  website_candidates: <Link className="w-4 h-4" />,
};

const ingestionStatusColors: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-600',
  processing: 'bg-blue-500/10 text-blue-500',
  completed: 'bg-primary/10 text-primary',
  approved: 'bg-primary/10 text-primary',
  flagged: 'bg-destructive/10 text-destructive',
  failed: 'bg-destructive/10 text-destructive',
  excluded: 'bg-muted text-muted-foreground',
};

// Helper to generate demo extracted items with flag detection
const generateDemoItems = (assetType: string): ExtractedItem[] => {
  if (assetType !== 'faq' && assetType !== 'document' && assetType !== 'website_candidates') return [];
  
  const demoContent = [
    { 
      content: "How do I reconcile my bank account in Xero?",
      type: 'faq_question' as const,
    },
    { 
      content: "To reconcile your bank account, go to Bank Accounts and click on the account you want to reconcile. Match transactions by clicking OK on matched items.",
      type: 'faq_answer' as const,
    },
    { 
      content: "What should I do if I receive an IRD audit notice?",
      type: 'faq_question' as const,
    },
    { 
      content: "If you receive an audit notice from IRD, you should immediately contact your accountant. We recommend gathering all relevant documentation and responding within the timeframe specified.",
      type: 'faq_answer' as const,
    },
    { 
      content: "We guarantee that our bookkeeping services will always be 100% accurate and you will never have any compliance issues.",
      type: 'paragraph' as const,
    },
    { 
      content: "The GST rate is currently 15% on most goods and services in New Zealand. Calculate your GST by multiplying the amount by 0.15.",
      type: 'paragraph' as const,
    },
    { 
      content: "You should definitely use the cash basis for accounting if your turnover is under $2 million.",
      type: 'paragraph' as const,
    },
    { 
      content: "Common expense categories include: Office supplies, Travel, Professional fees, Insurance, Utilities.",
      type: 'list_item' as const,
    },
  ];

  return demoContent.map((item, index) => {
    const flags: FlagType[] = [];
    
    Object.entries(FLAG_PATTERNS).forEach(([key, pattern]) => {
      const hasMatch = pattern.patterns.some(regex => {
        regex.lastIndex = 0; // Reset regex state
        return regex.test(item.content);
      });
      if (hasMatch) {
        flags.push(key as FlagType);
      }
    });

    return {
      id: `item-${index}`,
      content: item.content,
      type: item.type,
      status: 'pending' as const,
      flags,
    };
  });
};

const TenantAssetsTab: React.FC<TenantAssetsTabProps> = ({ tenantId }) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [activeView, setActiveView] = useState<'list' | 'scrutiny'>('list');
  const [crawling, setCrawling] = useState(false);

  // Get website profile data
  const websiteProfile = assets.find(a => a.asset_type === 'website_profile');
  const websiteCandidates = assets.find(a => a.asset_type === 'website_candidates');

  const triggerWebsiteCrawl = async () => {
    try {
      setCrawling(true);
      
      // Get tenant info for website URL
      const { data: tenant, error: tenantError } = await supabase
        .from('advisory_tenants')
        .select('website_url, name')
        .eq('id', tenantId)
        .single();

      if (tenantError || !tenant?.website_url) {
        toast.error('No website URL configured for this tenant');
        return;
      }

      toast.info('Starting website crawl...');
      
      const { data, error } = await supabase.functions.invoke('crawl-tenant-website', {
        body: {
          tenantId,
          websiteUrl: tenant.website_url,
          tenantName: tenant.name,
        },
      });

      if (error) {
        throw error;
      }

      toast.success(`Crawl completed: ${data.pages_crawled} pages, ${data.candidates_count} FAQ candidates`);
      fetchAssets();
    } catch (error) {
      console.error('Error crawling website:', error);
      toast.error('Failed to crawl website');
    } finally {
      setCrawling(false);
    }
  };
  const fetchAssets = async () => {
    try {
      const { data, error } = await supabase
        .from('tenant_assets')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Enhance assets with demo items if they don't have extracted_json
      const enhancedAssets = (data || []).map(asset => {
        const extractedJson = asset.extracted_json as { items?: any[] } | null;
        if (!extractedJson?.items && (asset.asset_type === 'faq' || asset.asset_type === 'document')) {
          const demoItems = generateDemoItems(asset.asset_type);
          const flaggedCount = demoItems.filter(i => i.flags.length > 0).length;
          return {
            ...asset,
            extracted_json: {
              items: demoItems,
              total_count: demoItems.length,
              approved_count: 0,
            },
            flagged_items: flaggedCount > 0 ? { count: flaggedCount } : null,
          };
        }
        return asset;
      });
      
      setAssets(enhancedAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [tenantId]);

  const handleApprove = async (assetId: string) => {
    try {
      const { error } = await supabase
        .from('tenant_assets')
        .update({ ingestion_status: 'completed', flagged_items: null })
        .eq('id', assetId);

      if (error) throw error;
      toast.success('Asset approved');
      fetchAssets();
    } catch (error) {
      console.error('Error approving asset:', error);
      toast.error('Failed to approve asset');
    }
  };

  const handleExclude = async (assetId: string) => {
    try {
      const { error } = await supabase
        .from('tenant_assets')
        .update({ 
          ingestion_status: 'excluded',
          flagged_items: { reason: 'Manually excluded by admin' }
        })
        .eq('id', assetId);

      if (error) throw error;
      toast.success('Asset excluded');
      fetchAssets();
    } catch (error) {
      console.error('Error excluding asset:', error);
      toast.error('Failed to exclude asset');
    }
  };

  const getFileName = (url: string) => {
    try {
      const parts = url.split('/');
      return parts[parts.length - 1] || 'Unknown file';
    } catch {
      return 'Unknown file';
    }
  };

  const hasScrutinyItems = (asset: Asset) => {
    return asset.asset_type === 'faq' || asset.asset_type === 'document' || asset.asset_type === 'website_candidates';
  };

  const getApprovalProgress = (asset: Asset) => {
    const extracted = asset.extracted_json as { items?: any[] } | null;
    if (!extracted?.items) return null;
    const total = extracted.items.length;
    const approved = extracted.items.filter(
      (i: any) => i.status === 'approved' || i.status === 'edited'
    ).length;
    return { approved, total };
  };

  const scrutinyAssets = assets.filter(hasScrutinyItems);
  const totalFlagged = scrutinyAssets.reduce((acc, asset) => {
    return acc + (asset.flagged_items?.count || 0);
  }, 0);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={(v) => setActiveView(v as 'list' | 'scrutiny')}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="list" className="gap-2">
              <FolderOpen className="w-4 h-4" />
              All Assets
            </TabsTrigger>
            <TabsTrigger value="scrutiny" className="gap-2">
              <Search className="w-4 h-4" />
              Ingestion Scrutiny
              {totalFlagged > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                  {totalFlagged}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={fetchAssets}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* List View */}
        <TabsContent value="list" className="mt-4">
          {/* Website Profile Card */}
          {websiteProfile && (
            <Card className="mb-6 border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    <CardTitle className="text-base">Website Profile</CardTitle>
                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                      Auto-extracted
                    </Badge>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={triggerWebsiteCrawl}
                    disabled={crawling}
                  >
                    {crawling ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Re-crawl Website
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Firm Name</p>
                    <p className="font-medium">{websiteProfile.extracted_json?.firm_name || 'Not extracted'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Services</p>
                    <p className="font-medium">{websiteProfile.extracted_json?.services?.length || 0} identified</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Pages Crawled</p>
                    <p className="font-medium">{websiteProfile.extracted_json?.pages_crawled || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Last Crawled</p>
                    <p className="font-medium text-xs">
                      {websiteProfile.extracted_json?.crawled_at 
                        ? new Date(websiteProfile.extracted_json.crawled_at).toLocaleString()
                        : 'Unknown'}
                    </p>
                  </div>
                </div>
                {websiteProfile.extracted_json?.about_summary && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-muted-foreground mb-1">About Summary</p>
                    <p className="text-sm">{websiteProfile.extracted_json.about_summary}</p>
                  </div>
                )}
                {websiteProfile.extracted_json?.services?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Services Identified</p>
                    <div className="flex flex-wrap gap-2">
                      {websiteProfile.extracted_json.services.slice(0, 8).map((service: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                      {websiteProfile.extracted_json.services.length > 8 && (
                        <Badge variant="secondary" className="text-xs">
                          +{websiteProfile.extracted_json.services.length - 8} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Website Candidates Alert */}
          {websiteCandidates && websiteCandidates.ingestion_status === 'flagged' && (
            <Card className="mb-6 border-destructive/30 bg-destructive/5">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    <div>
                      <p className="font-medium">Website FAQ Candidates Require Review</p>
                      <p className="text-sm text-muted-foreground">
                        {websiteCandidates.extracted_json?.total_count || 0} FAQ pairs extracted from website. 
                        These must be reviewed before being included in bot knowledge.
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setSelectedAsset(websiteCandidates);
                      setActiveView('scrutiny');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Review Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No website data - show crawl button */}
          {!websiteProfile && !crawling && (
            <Card className="mb-6 border-dashed">
              <CardContent className="py-8 text-center">
                <Globe className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-medium mb-1">No Website Data</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Crawl the client's website to extract firm profile and FAQ candidates
                </p>
                <Button onClick={triggerWebsiteCrawl} disabled={crawling}>
                  {crawling ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="w-4 h-4 mr-2" />
                  )}
                  Crawl Website
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold">{assets.length}</p>
                <p className="text-sm text-muted-foreground">Total Assets</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-primary">
                  {assets.filter(a => a.ingestion_status === 'completed' || a.ingestion_status === 'approved').length}
                </p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-muted-foreground">
                  {assets.filter(a => a.ingestion_status === 'pending' || a.ingestion_status === 'processing').length}
                </p>
                <p className="text-sm text-muted-foreground">Processing</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-destructive">
                  {assets.filter(a => a.ingestion_status === 'flagged').length + totalFlagged}
                </p>
                <p className="text-sm text-muted-foreground">Need Review</p>
              </CardContent>
            </Card>
          </div>

          {/* Assets Table */}
          <Card>
            <CardHeader>
              <CardTitle>Uploaded Assets</CardTitle>
            </CardHeader>
            <CardContent>
              {assets.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No assets uploaded</h3>
                  <p className="text-muted-foreground mt-1">
                    Assets will appear here once the client completes the intake form
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>File</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Uploaded</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assets.map((asset) => {
                        const progress = getApprovalProgress(asset);
                        return (
                          <TableRow key={asset.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {assetTypeIcons[asset.asset_type] || <FileText className="w-4 h-4" />}
                                <span className="capitalize">{asset.asset_type.replace('_', ' ')}</span>
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {getFileName(asset.file_url)}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="secondary"
                                className={ingestionStatusColors[asset.ingestion_status] || 'bg-muted text-muted-foreground'}
                              >
                                {asset.ingestion_status === 'processing' && (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                )}
                                {asset.flagged_items?.count > 0 && asset.ingestion_status === 'pending' && (
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                )}
                                {asset.ingestion_status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {progress ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-primary transition-all"
                                      style={{ width: `${(progress.approved / progress.total) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {progress.approved}/{progress.total}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(asset.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {hasScrutinyItems(asset) && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      setSelectedAsset(asset);
                                      setActiveView('scrutiny');
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem asChild>
                                      <a href={asset.file_url} target="_blank" rel="noopener noreferrer">
                                        <Download className="w-4 h-4 mr-2" />
                                        Download
                                      </a>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleApprove(asset.id)}>
                                      <Check className="w-4 h-4 mr-2" />
                                      Approve All
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleExclude(asset.id)}>
                                      <X className="w-4 h-4 mr-2" />
                                      Exclude
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scrutiny View */}
        <TabsContent value="scrutiny" className="mt-4">
          <div className="space-y-6">
            {/* Flag Legend */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Flag Pattern Legend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(FLAG_PATTERNS).map(([key, pattern]) => {
                    const Icon = pattern.icon;
                    return (
                      <div key={key} className={`p-3 rounded-lg ${pattern.bgColor}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-4 h-4 ${pattern.color}`} />
                          <span className={`text-sm font-medium ${pattern.color}`}>
                            {pattern.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {pattern.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Assets with Scrutiny */}
            {scrutinyAssets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No documents to scrutinize</h3>
                  <p className="text-muted-foreground mt-1">
                    Upload FAQ or document files to enable content scrutiny
                  </p>
                </CardContent>
              </Card>
            ) : (
              scrutinyAssets.map(asset => (
                <Card key={asset.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {assetTypeIcons[asset.asset_type]}
                        <div>
                          <CardTitle className="text-base">
                            {getFileName(asset.file_url)}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground capitalize">
                            {asset.asset_type.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant="secondary"
                        className={ingestionStatusColors[asset.ingestion_status]}
                      >
                        {asset.ingestion_status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <IngestionScrutinyPanel 
                      asset={asset} 
                      onUpdate={fetchAssets}
                    />
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantAssetsTab;
